import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {Button, TActionButtonOption} from '@edna/components';
import {Stack} from '@edna/components/primitives';

import {Text} from 'src/components/primitives';
import {AddButton, AddButtonText, AddIcon, PermissionCheck} from 'src/containers/AddButton';
import {showReminderModal, useCheckCompanyDetailsFilled} from 'src/containers/CompanyDetails';
import {EResellerIds, useReseller} from 'src/containers/User';
import {CardIcon, DocumentationIcon} from 'src/icons';

import {
  StripeAutopaymentConnectButton,
  StripeAutopaymentInfo,
  usePayKeeperAutopaymentEnabled,
  useStripeAutopaymentButtonOption,
  useStripeAutopaymentEnabled,
} from './Autopayment';
import {
  PayKeeperAutopaymentInfo,
  PaykeeperAutopaymentConnectButton,
  usePaykeeperAutopaymentButtonOption,
} from './Autopayment/PayKeeper';
import {InvoiceButton, showInvoiceModal} from './Invoice';
import {useInvoiceEnabled} from './Invoice/useInvoiceEnabled';
import {
  PayKeeperOnetimePaymentButton,
  PayKeeperOnetimePaymentInfo,
  StripeOnetimePaymentButton,
  showPayKeeperOnetimePaymentSettingsModal,
  showStripeOnetimePaymentSettingsModal,
  usePayKeeperOnetimePaymentEnabled,
  useStripeOnetimePaymentEnabled,
} from './OnetimePayment';
import * as S from './style';

const BalanceReplenishment = React.memo(() => {
  const {t} = useTranslation();

  const isInvoiceEnabled = useInvoiceEnabled();
  const isStripeAutopaymentEnabled = useStripeAutopaymentEnabled();
  const isPayKeeperAutopaymentEnabled = usePayKeeperAutopaymentEnabled();
  const isStripeOnetimePaymentEnabled = useStripeOnetimePaymentEnabled();
  const isPayKeeperOnetimePaymentEnabled = usePayKeeperOnetimePaymentEnabled();

  const isCompanyDetailsFilled = useCheckCompanyDetailsFilled();
  const action = isCompanyDetailsFilled ? null : showReminderModal;

  return (
    <>
      {isStripeAutopaymentEnabled && <StripeAutopaymentInfo />}
      {isPayKeeperOnetimePaymentEnabled && <PayKeeperOnetimePaymentInfo />}
      {isPayKeeperAutopaymentEnabled && <PayKeeperAutopaymentInfo />}
      <Stack size="2">
        <Text color="grey600" size="L" weight={500}>
          {t('BalanceReplenishment:topUpBalance')}
        </Text>
        <Stack direction="ROW" align="center" size="2">
          {isInvoiceEnabled && <InvoiceButton action={action} />}
          {isPayKeeperOnetimePaymentEnabled && <PayKeeperOnetimePaymentButton action={action} />}
          {isPayKeeperAutopaymentEnabled && <PaykeeperAutopaymentConnectButton action={action} />}
          {isStripeOnetimePaymentEnabled && <StripeOnetimePaymentButton action={action} />}
          {isStripeAutopaymentEnabled && <StripeAutopaymentConnectButton action={action} />}
        </Stack>
      </Stack>
    </>
  );
});

const TopUpButton = React.memo<{options: TActionButtonOption[]}>(({options}) => {
  const {t} = useTranslation();
  const isCompanyDetailsFilled = useCheckCompanyDetailsFilled();

  if (!options.length) {
    return null;
  }

  const {handler, content} = options[0];

  if (!isCompanyDetailsFilled) {
    return (
      <Button appearance="primary" onClick={showReminderModal}>
        {options.length === 1 ? (
          content
        ) : (
          <>
            <AddIcon />
            <AddButtonText>{t('BalanceReplenishment:actions.topUp')}</AddButtonText>
          </>
        )}
      </Button>
    );
  }

  if (options.length > 1) {
    return (
      <AddButton permission={PermissionCheck} options={options}>
        <AddIcon />
        <AddButtonText>{t('BalanceReplenishment:actions.topUp')}</AddButtonText>
      </AddButton>
    );
  }

  return (
    <AddButton onClick={handler} permission={PermissionCheck}>
      {content}
    </AddButton>
  );
});

const MFMButton = React.memo(() => {
  const {t} = useTranslation();

  const stripeAutopaymentOption = useStripeAutopaymentButtonOption();
  const isStripeOnetimePaymentEnabled = useStripeOnetimePaymentEnabled();
  const isInvoiceEnabled = useInvoiceEnabled();

  const options = React.useMemo(() => {
    const allowedOptions: TActionButtonOption[] = [];

    if (isInvoiceEnabled) {
      allowedOptions.push({
        content: (
          <>
            <S.OptionIcon as={DocumentationIcon} />
            {t('BalanceReplenishment:actions.generateInvoice')}
          </>
        ),
        handler: showInvoiceModal,
      });
    }

    if (stripeAutopaymentOption) {
      allowedOptions.push(stripeAutopaymentOption);
    }

    if (isStripeOnetimePaymentEnabled) {
      allowedOptions.push({
        content: (
          <>
            <S.OptionIcon as={CardIcon} />
            {t('BalanceReplenishment:actions.payByCard')}
          </>
        ),
        handler: showStripeOnetimePaymentSettingsModal,
      });
    }

    return allowedOptions;
  }, [t, stripeAutopaymentOption, isStripeOnetimePaymentEnabled, isInvoiceEnabled]);

  return <TopUpButton options={options} />;
});

const BaseButton = React.memo(() => {
  const {t} = useTranslation();
  const {id: supportTenantId} = useParams<'id'>();

  const isPayKeeperOnetimePaymentEnabled = usePayKeeperOnetimePaymentEnabled();
  const payKeeperAutopaymentOption = usePaykeeperAutopaymentButtonOption();

  const options = React.useMemo(() => {
    const allowedOptions: TActionButtonOption[] = [
      {
        content: (
          <>
            <S.OptionIcon as={DocumentationIcon} />
            {t('BalanceReplenishment:actions.generateInvoice')}
          </>
        ),
        handler: showInvoiceModal,
      },
    ];

    if (supportTenantId) {
      return allowedOptions;
    }

    if (isPayKeeperOnetimePaymentEnabled) {
      allowedOptions.push({
        content: (
          <>
            <S.OptionIcon as={CardIcon} />
            {t('BalanceReplenishment:actions.payByCard')}
          </>
        ),
        handler: showPayKeeperOnetimePaymentSettingsModal,
      });
    }

    if (payKeeperAutopaymentOption) {
      allowedOptions.push(payKeeperAutopaymentOption);
    }

    return allowedOptions;
  }, [t, supportTenantId, payKeeperAutopaymentOption, isPayKeeperOnetimePaymentEnabled]);

  return <TopUpButton options={options} />;
});

const BalanceReplenishmentButton = React.memo(() => {
  const isMFM = useReseller(EResellerIds.MFM_ID);

  if (isMFM) {
    return <MFMButton />;
  }

  return <BaseButton />;
});

export {BalanceReplenishment, BalanceReplenishmentButton};
